import React, { useEffect, useMemo, useState } from 'react';
import {
  Card,
  Select,
  Button,
  Space,
  Table,
  Alert,
  Tag,
  Row,
  Col,
  Empty,
  Tooltip,
  Spin,
  Typography,
  theme,
} from 'antd';
import {
  SwapOutlined,
  BarChartOutlined,
  TrophyOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons';
import { useAppDispatch, useAppSelector } from 'store';
import { compareCampaigns, selectCampaignComparison, selectLoadingFor } from 'store/slices/stats.slice';
import { pushApi } from 'api/endpoints/push.api';
import { Campaign } from 'types/push.types';
import { AppStatsCard } from './AppStatsCard';
import { extractList, leaderBy, maxBy } from './compareHelpers';
import styles from '../../pages/Statistics/statistics.module.css';

const { Text } = Typography;

const MIN_SELECTED = 2;
const MAX_SELECTED = 5;

interface CompareCampaignsProps {
  appId: number;
}

const sentOf = (row: any) => row.stats?.total_statistics?.sent ?? 0;
const clickedOf = (row: any) => row.stats?.total_statistics?.clicked ?? 0;
const ctrOf = (row: any) => row.stats?.metrics?.ctr ?? 0;
const registrationsOf = (row: any) => row.stats?.total_statistics?.attributed_registrations ?? 0;
const depositsOf = (row: any) => row.stats?.total_statistics?.attributed_deposits ?? 0;

const ctrTagColor = (ctr: number) => {
  if (ctr > 5) return 'success';
  if (ctr > 2) return 'processing';
  return 'default';
};

export const CompareCampaigns: React.FC<CompareCampaignsProps> = ({ appId }) => {
  const dispatch = useAppDispatch();
  const { token } = theme.useToken();
  const comparison = useAppSelector(selectCampaignComparison);
  const comparing = useAppSelector(selectLoadingFor('campaignComparison'));

  const [selected, setSelected] = useState<number[]>([]);
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [listLoading, setListLoading] = useState(false);

  useEffect(() => {
    if (!appId) return undefined;

    let cancelled = false;
    setSelected([]);
    setListLoading(true);

    pushApi
      .listCampaigns(appId, 100, 0)
      .then((response) => {
        if (!cancelled) setCampaigns(extractList<Campaign>(response));
      })
      .catch(() => {
        if (!cancelled) setCampaigns([]);
      })
      .finally(() => {
        if (!cancelled) setListLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [appId]);

  const handleCompare = () => {
    if (selected.length < MIN_SELECTED || selected.length > MAX_SELECTED) return;
    dispatch(compareCampaigns(selected));
  };

  const campaignOptions = useMemo(
    () => campaigns.map((campaign) => ({ value: campaign.id, label: campaign.name })),
    [campaigns],
  );

  const rows = comparison?.campaigns ?? [];
  const bestSent = maxBy(rows, sentOf);
  const bestClicked = maxBy(rows, clickedOf);
  const bestCtr = maxBy(rows, ctrOf);
  const leader = leaderBy(rows, ctrOf);

  const bestMark = (isBest: boolean) =>
    isBest ? <TrophyOutlined style={{ marginLeft: 4, color: token.colorPrimary }} /> : null;

  const columns = [
    {
      title: 'Кампания',
      dataIndex: 'name',
      key: 'name',
      fixed: 'left' as const,
      width: 200,
      render: (name: string, record: any) => (
        <div>
          <Text strong>{name}</Text>
          <div>
            <Text type="secondary" style={{ fontSize: 11 }}>
              ID: {record.id}
            </Text>
          </div>
        </div>
      ),
    },
    {
      title: 'Отправлено',
      key: 'sent',
      width: 130,
      sorter: (a: any, b: any) => sentOf(a) - sentOf(b),
      render: (_: unknown, record: any) => {
        const value = sentOf(record);
        return (
          <Text strong={value === bestSent}>
            {value.toLocaleString('ru-RU')}
            {bestMark(value === bestSent)}
          </Text>
        );
      },
    },
    {
      title: 'Клики',
      key: 'clicked',
      width: 120,
      sorter: (a: any, b: any) => clickedOf(a) - clickedOf(b),
      render: (_: unknown, record: any) => {
        const value = clickedOf(record);
        return (
          <Text strong={value === bestClicked}>
            {value.toLocaleString('ru-RU')}
            {bestMark(value === bestClicked)}
          </Text>
        );
      },
    },
    {
      title: 'CTR',
      key: 'ctr',
      width: 120,
      sorter: (a: any, b: any) => ctrOf(a) - ctrOf(b),
      render: (_: unknown, record: any) => {
        const value = ctrOf(record);
        return (
          <span>
            <Tag color={ctrTagColor(value)}>{value.toFixed(2)}%</Tag>
            {bestMark(value === bestCtr)}
          </span>
        );
      },
    },
    {
      title: 'Регистрации',
      key: 'registrations',
      width: 120,
      sorter: (a: any, b: any) => registrationsOf(a) - registrationsOf(b),
      render: (_: unknown, record: any) => (
        <Text>{registrationsOf(record).toLocaleString('ru-RU')}</Text>
      ),
    },
    {
      title: 'Депозиты',
      key: 'deposits',
      width: 120,
      sorter: (a: any, b: any) => depositsOf(a) - depositsOf(b),
      render: (_: unknown, record: any) => (
        <Text>{depositsOf(record).toLocaleString('ru-RU')}</Text>
      ),
    },
  ];

  return (
    <Card
      title={
        <span className={styles.chartTitle}>
          <div className={styles.chartTitleIcon}>
            <SwapOutlined />
          </div>
          Сравнение кампаний
        </span>
      }
      className={styles.statsCard}
      extra={
        <Tooltip title={`Можно сравнить от ${MIN_SELECTED} до ${MAX_SELECTED} кампаний`}>
          <InfoCircleOutlined />
        </Tooltip>
      }
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Row gutter={[12, 12]} align="bottom">
          <Col xs={24} md={18}>
            <Text type="secondary" style={{ display: 'block', fontSize: 12, marginBottom: 4 }}>
              Кампании для сравнения
            </Text>
            <Select
              mode="multiple"
              value={selected}
              onChange={setSelected}
              options={campaignOptions}
              optionFilterProp="label"
              style={{ width: '100%' }}
              placeholder={listLoading ? 'Загрузка кампаний...' : 'Выберите кампании'}
              className={styles.modernSelect}
              maxTagCount={MAX_SELECTED}
              allowClear
              size="small"
              loading={listLoading}
              notFoundContent={
                listLoading ? (
                  <Spin size="small" />
                ) : (
                  <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Нет кампаний" />
                )
              }
            />
          </Col>
          <Col xs={24} md={6}>
            <Button
              type="primary"
              size="small"
              block
              onClick={handleCompare}
              disabled={selected.length < MIN_SELECTED || selected.length > MAX_SELECTED}
              loading={comparing}
              icon={<BarChartOutlined />}
              className={styles.modernButton}
            >
              Сравнить
            </Button>
          </Col>
        </Row>

        {selected.length > 0 && selected.length < MIN_SELECTED && (
          <Alert
            message={`Выберите минимум ${MIN_SELECTED} кампании`}
            type="info"
            showIcon
            className={styles.modernAlert}
          />
        )}

        {rows.length > 0 ? (
          <>
            <Row gutter={[12, 12]}>
              <Col xs={24} sm={8}>
                <AppStatsCard
                  title="Кампаний в сравнении"
                  value={rows.length}
                  icon={<SwapOutlined />}
                  className={styles.info}
                  compact
                />
              </Col>
              <Col xs={24} sm={8}>
                <AppStatsCard
                  title="Лучший CTR"
                  value={`${(bestCtr ?? 0).toFixed(2)}%`}
                  icon={<BarChartOutlined />}
                  className={styles.success}
                  compact
                />
              </Col>
              <Col xs={24} sm={8}>
                <AppStatsCard
                  title="Лидер по CTR"
                  value={leader?.name ?? '—'}
                  icon={<TrophyOutlined />}
                  compact
                />
              </Col>
            </Row>

            <Table
              columns={columns}
              dataSource={rows}
              rowKey="id"
              pagination={false}
              scroll={{ x: 900 }}
              size="small"
            />
          </>
        ) : (
          <Empty
            description="Выберите кампании и нажмите «Сравнить»"
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        )}

        {comparison?.warnings?.inaccessible_campaigns?.length ? (
          <Alert
            message={comparison.warnings.message}
            description={`Недоступные кампании: ${comparison.warnings.inaccessible_campaigns.join(', ')}`}
            type="warning"
            showIcon
            className={styles.modernAlert}
          />
        ) : null}
      </Space>
    </Card>
  );
};